// A full-screen overlay shown while a Computer player takes its turn - only
// needed for a game with no dartboard already mounted as its own scoring
// input (501/x01, today - games/shared/x01-game-controller.js). Mounts the
// real dartboard.svg artwork read-only and reveals a CPU's already-simulated
// darts (see games/shared/x01-ai.js's simulateAiTurn) one at a time with a
// pause in between, so a turn feels like a turn instead of the score just
// appearing.
//
// Unlike core/modal.js's modals, this isn't backed by any static markup and
// has no back-button guard - it creates its own DOM fresh each call and
// removes it when done, since there's nothing here for a user to dismiss
// early (the turn's already been decided; this is purely a reveal).
import { mountDartboardFullBoard, drawDartMark, drawAimMarker } from './dartboard-input.js';
import { isAimDebugMode, cpuDelayOverride } from '../core/debug.js';

// How long each dart's landing stays on screen before the next one is
// revealed - the one knob this whole animation is tuned by (see the AI
// implementation plan's "configurable pause" note). Also used as the pause
// before the first dart (so the empty board registers before anything
// lands on it) and after the final outcome line, before the overlay closes.
export const AI_THROW_PAUSE_MS = 700;

// Every pause in this file goes through here, so ?cpuDelay=<ms> (see
// core/debug.js) can override or remove them all at once.
function wait(ms) {
    const override = cpuDelayOverride();
    return new Promise(resolve => setTimeout(resolve, override ?? ms));
}

function labelForResult(result) {
    if (result.ring === 'miss') return 'MISS';
    if (result.ring === 'bullseye') return 'BULL';
    if (result.ring === 'outerBull') return '25';
    const prefix = { triple: 'T', double: 'D' }[result.ring] || '';
    return `${prefix}${result.number}`;
}

// Creates and shows the overlay and mounts the read-only board into it, but
// draws no darts yet - callers await waitBeforeFirstDart() before revealing
// the first one, so the empty board actually registers a beat before
// anything lands on it, same as a real player stepping up to throw. Returns
// a handle the caller reveals darts through (see revealCpuDart).
export function showCpuThrowOverlay(playerName) {
    const el = document.createElement('div');
    el.className = 'cpu-throw-overlay';
    el.innerHTML = `
        <div class="cpu-throw-panel">
            <div class="cpu-throw-status">${playerName} is throwing&hellip;</div>
            <div class="cpu-throw-board" id="cpuThrowBoard"></div>
            <div class="cpu-throw-readout" id="cpuThrowReadout">&nbsp;</div>
        </div>
    `;
    document.body.appendChild(el);
    const boardMount = el.querySelector('#cpuThrowBoard');
    mountDartboardFullBoard(boardMount, { readOnly: true });
    return { el, boardMount, readoutEl: el.querySelector('#cpuThrowReadout') };
}

// The pause between the board first appearing and its first dart landing -
// same AI_THROW_PAUSE_MS as every other pause here, so the whole reveal
// (board appears, first dart, second, third, outcome, close) keeps one
// consistent rhythm rather than the first beat feeling rushed next to the
// rest.
export function waitBeforeFirstDart() {
    return wait(AI_THROW_PAUSE_MS);
}

// Draws one already-simulated dart's actual landing point on the overlay's
// board (see styles.css's .cpu-throw-board .dart-tap-mark for the pop-in
// animation that draws the eye to where it just landed) and updates the
// readout - no pause of its own, so a caller that's just mirroring another
// client's already-paced turn (an observer - see
// games/shared/x01-game-controller.js's syncObserverCpuOverlay) can draw a
// dart the instant it hears about it, instead of re-timing the reveal
// itself. `readout` is whatever the caller wants shown after the aim label -
// x01 and most Halve It rounds pass their running total, but a round can
// pass anything else that's more informative instead (Halve It's 3 Colors
// round shows the color a dart actually landed as, e.g. "T20 — Red", since
// its own running total is mostly 0 until the last dart - see
// games/halveit/controller.js's own dartReadout). revealCpuDart (below) is
// the version the client actually running the turn uses, which paces itself
// with this same AI_THROW_PAUSE_MS.
export function drawCpuDart(handle, thrown, readout) {
    drawDartMark(handle.boardMount, thrown.actualPoint.x, thrown.actualPoint.y);
    handle.readoutEl.textContent = `${labelForResult(thrown.result)} — ${readout}`;
}

// Marks where this dart was actually aimed, numbered by which dart of the
// turn it is (1-3) - a no-op outside ?debugAim=1 (see core/debug.js's
// isAimDebugMode), so every caller can call this unconditionally rather
// than checking debug mode itself. Generic across every CPU-supported game:
// any controller revealing a plan built from ai-throw-simulator.js's own
// simulateThrow() already has a real `aimPoint` on each thrown dart to show.
export function showAimMarker(handle, thrown, dartNumber) {
    if (!isAimDebugMode()) return;
    drawAimMarker(handle.boardMount, thrown.aimPoint.x, thrown.aimPoint.y, dartNumber);
}

// Shows where the dart was aimed (debug only - see showAimMarker), pauses
// so that registers before the dart actually lands, then draws the real
// landing and waits AI_THROW_PAUSE_MS again - callers await this once per
// dart so a whole turn reveals at a human pace instead of all at once.
export async function revealCpuDart(handle, thrown, readout, dartNumber) {
    showAimMarker(handle, thrown, dartNumber);
    if (isAimDebugMode()) await wait(AI_THROW_PAUSE_MS);
    drawCpuDart(handle, thrown, readout);
    return wait(AI_THROW_PAUSE_MS);
}

// Shows a final outcome line (e.g. "BUST", "140") with no pause of its own -
// see drawCpuDart's own comment on why an observer mirroring another
// client's turn needs the un-paced version.
export function setCpuThrowOutcome(handle, outcomeLabel) {
    handle.readoutEl.textContent = outcomeLabel;
}

// Shows the final outcome line and keeps the board open for one more pause
// so it actually registers, then tears the whole overlay down.
export async function finishCpuThrowOverlay(handle, outcomeLabel) {
    setCpuThrowOutcome(handle, outcomeLabel);
    await wait(AI_THROW_PAUSE_MS);
    handle.el.remove();
}

// The pause a Computer player's turn adds before its *own* overlay opens,
// when the player who just went was also a Computer - without this,
// back-to-back CPU turns replace one overlay with the next fast enough that
// the real board underneath is never actually seen, and it reads as one
// overlay that never closes rather than a sequence of distinct turns. Never
// used to delay a turn's actual commit (see x01-game-controller.js's
// maybeRunCpuTurn) - only ever this CPU's own overlay from appearing, so a
// human up next always sees their opponent's score the moment that
// opponent's turn actually ends, with nothing extra to wait through.
export function waitBetweenCpuTurns() {
    return wait(AI_THROW_PAUSE_MS);
}
